import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { CacheHelper } from "@/Common/Helpers/CacheHelper";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { BillingInbox } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { Server } from "@/Common/Operations/operations.server";
import { GeneratePayableBillsCsvResponse } from "../Api/Inbox/Dto/Response/GeneratePayableBillsCsvResponse";
import { IJSalesInboxModel } from "../Models/JSONInterfaces/IJSalesInboxModel";
import { IJTransactionInboxModel } from "../Models/JSONInterfaces/IJTransactionInboxModel";
import { PayableInboxFilterSettings } from "../Models/Settings/PayableInboxFilterSettings";

class BillingHelper {
    public static getDefaultTransactionInboxModel(): IJTransactionInboxModel {
        var billingHelper: IJTransactionInboxModel = {
            GlobalTransactionsSearchText: "",
            GlobalTransactionsSearchPlaceholder: "",
            TableData: Immutable.List([]),
            ShowTransactionsDisplaySettings: false,
            SelectAllSitesChecked: false,
            SelectAllStatusChecked: false,
            Sites: CacheHelper.LoadSitesFromCache(CheckboxTarget.Myle_Billing_Transactions_Sites).toImmutable(),
            ShowOnlySelectedSites: true,
            SelectAllProviderChecked: false,
            SelectAllBilledByChecked: false,
            SelectAllPaymentChecked: false,
            SelectedPageSizeOption: 50,
            PageSizeOptions: Immutable.List([50, 100, 150]),
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            ProviderSearchText: "",
            DateFrom: this.GetSearchDefaultFromDate(),
            DateTo: this.GetSearchDefaultToDate(),
            VisitDateFrom: moment(new Date()).startOf("day").toDate(),
            VisitDateTo: moment(new Date()).endOf("day").toDate(),
            BilledBySearchText: "",
            ProviderGlobalCache: Immutable.List([]),
            ProviderFiltered: Immutable.List([]),
            BilledByFiltered: Immutable.List([]),
            BilledByGlobalCache: Immutable.List([]),
            BillingPopupIsVisible: false,
            ColumnOrderingPopupOpen: false,
            DiscountSum: 0,
            GSTSum: 0,
            PaymentMethod: Immutable.List([]),
            QSTSum: 0,
            HSTSum: 0,
            GenericTaxSum: 0,
            Status: Immutable.List([]),
            SubtotalSum: 0,
            TotalBillSum: 0,
            Validity: Immutable.List([])
        };

        return billingHelper;
    }

    public static GetDefaultSalesInboxModel(): IJSalesInboxModel {
        var salesModel: IJSalesInboxModel =
        {
            GlobalSalesSearchPlaceholder: "",
            GlobalSalesSearchText: "",
            TableData: Immutable.List([]),
            ShowSalesDisplaySettings: false,
            SelectAllSitesChecked: false,
            SelectAllStatusChecked: false,
            Sites: CacheHelper.LoadSitesFromCache(CheckboxTarget.Myle_Billing_Sales_Sites).toImmutable(),
            ShowOnlySelectedSites: false,
            SelectAllProviderChecked: false,
            SelectAllBilledByChecked: false,
            SelectAllPaymentChecked: false,
            SelectedPageSizeOption: 50,
            PageSizeOptions: Immutable.List([50, 100, 150]),
            SelectedPageNumber: 1,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            CurrentPage: 0,
            ProviderSearchText: "",
            DateFrom: this.GetSearchDefaultFromDate(),
            DateTo: this.GetSearchDefaultToDate(),
            VisitFromDate: null,
            VisitToDate: null,
            BilledBySearchText: "",
            ProviderGlobalCache: Immutable.List([]),
            ProviderFiltered: Immutable.List([]),
            BilledByFiltered: Immutable.List([]),
            BilledByGlobalCache: Immutable.List([]),
            ColumnOrderingPopupOpen: false,
            DiscountSum: 0,
            GSTSum: 0,
            PaymentMethod: Immutable.List([]),
            QSTSum: 0,
            Status: Immutable.List([]),
            SubtotalSum: 0,
            TotalBillSum: 0,
            BillingPopupIsVisible: false
        };

        return salesModel;
    }

    public static GetSearchDefaultFromDate(): Date {
        return moment().subtract(5, "years").startOf("day").toDate();
    }

    public static GetSearchDefaultToDate(): Date {
        return moment().endOf("day").toDate();
    }

    public static async exportPayableCsv(filterSettings: PayableInboxFilterSettings): Promise<void> {
        const response = await this.generatePayableCsv(filterSettings);
        if (!!response)
            this.downloadCsvFileFromUrl(response.FileId, "ReceivableBills");
    }

    public static async exportBillingCsv(request: any, context: BillingInbox): Promise<void> {
        const fileName = context === BillingInbox.Sales ? "Sales" : "Transactions";
        const response = await this.generateBillingCsv(request);
        if (!!response)
            this.downloadCsvFileFromUrl(response.FileId, fileName);
    }

    private static async generatePayableCsv(filterSettings: PayableInboxFilterSettings): Promise<GeneratePayableBillsCsvResponse> {
        const response = await Client.postAsync<GeneratePayableBillsCsvResponse>(
            Server.Myle_Billing_GeneratePayableBillsCsv,
            filterSettings
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return null;
        }
        return response.data;
    }

    private static async generateBillingCsv(request: any): Promise<GeneratePayableBillsCsvResponse> {
        const response = await Client.postAsync<{ FileId: string }>(
            Server.Myle_GenerateBillingCSV,
            request
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return null;
        }
        return response.data;
    }

    private static downloadCsvFileFromUrl(fileId: string, fileName: string): void {
        if (fileId) {
            const downloadUrl = apiSettings.buildAppUrl(`api/billing/fetchCsv/${fileId}`, { auth: true, noCache: true, params: { DispositionFileName: `${fileName}.csv` } });
            window.open(downloadUrl, "_blank");
        }
    }

    private static privateBillPermission(): boolean { return sessionStore.hasPermission(EnumMyleLoginPermission.PrivateBillingPermission); }
    private static publicBillPermission(): boolean { return sessionStore.hasPermission(EnumMyleLoginPermission.PublicBillingPermission); }
    private static parentBillPermission(): boolean { return sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermissionCurrentUserOnly); }
    private static globalBillPermission(): boolean { return sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission); }
    private static isBillingOnlySelected = (): boolean => (BillingHelper.parentBillPermission
        && !BillingHelper.privateBillPermission()
        && !BillingHelper.publicBillPermission()
    );

    public static isPrivateBillPermission(): boolean {
        return (BillingHelper.globalBillPermission() && (BillingHelper.isBillingOnlySelected() || BillingHelper.privateBillPermission()))
            || (!BillingHelper.globalBillPermission() && BillingHelper.privateBillPermission())
            || (BillingHelper.globalBillPermission() && !BillingHelper.parentBillPermission() && !BillingHelper.privateBillPermission())
            || (!BillingHelper.globalBillPermission() && BillingHelper.isBillingOnlySelected());
    }

    public static isPublicBillPermission(): boolean {
        return (BillingHelper.globalBillPermission() && (BillingHelper.isBillingOnlySelected() || BillingHelper.publicBillPermission()))
            || (!BillingHelper.globalBillPermission() && BillingHelper.publicBillPermission())
            || (BillingHelper.globalBillPermission() && !BillingHelper.parentBillPermission() && !BillingHelper.publicBillPermission())
            || (!BillingHelper.globalBillPermission() && BillingHelper.isBillingOnlySelected());
    }
}

export { BillingHelper };
